import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
} from 'recharts';
import { getColorByIndex } from '../../utils/colors';
import { percentFormatter } from '../../utils/formatters';
import { ChartDataItem, TypedChartProps } from './Chart.component';
import ChartLegentContentComponent from './ChartLegendContent.component';

const StyledDonutChartContainer = styled.div`
  aspect-ratio: 1;
  max-height: 100%;
  max-width: 100%;
  width: 100%;
  * {
    outline: none;
  }
`;

const LABEL_COLOR = 'black';

export const renderLegendLabelFormatter = (
  label: string,
  data: { label: string;[key: string]: any }[],
  commonCount: number | undefined,
) => {
  const value = data.find(d => d.label === label)?.value;
  let formattedValue = '';
  if (value !== undefined)
    formattedValue = `${label}: ${percentFormatter(value, commonCount)}`;
  return <span>{formattedValue}</span>;
};

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    value,
    commonCount,
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 8) * cos;
  const my = cy + (outerRadius + 15) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  const payloadLabelContent = [];
  const splittedLabel = payload.label.toString().split(' ');
  const step = 20;
  const baseY = cy - (splittedLabel.length - 1) * step / 2;
  for (let index = 0; index < splittedLabel.length; index++) {
    const y = baseY + (index * step);
    payloadLabelContent.push((<text x={cx} y={y} dy={8} textAnchor="middle" fill={LABEL_COLOR}>
      {splittedLabel[index]}
    </text>));
  }
  const valueForRender = percentFormatter(value, commonCount);
  return (
    <g>
      {payloadLabelContent}
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill={LABEL_COLOR}
      >
        {valueForRender}
      </text>
    </g>
  );
};

const renderCustomizedLabel = (payload: any) => {
  const { cx, value, x, y } = payload;
  return (
      <text x={x} y={y} textAnchor={x > cx ? 'start' : 'end'} fill={LABEL_COLOR}>
        {value}
      </text>
  );
};

const DonutChartComponent = ({
  data,
  onClick,
  showLabels,
  legendPortalRef,
  isAnimationActive = true,
  commonCount,
  showLegend = true,
}: TypedChartProps) => {
  const [activeIndex, setActiveIndex] = useState<number | undefined>();

  const handleClick = ({ payload }: any) => {
    const dataItem = payload.payload as ChartDataItem;
    if (onClick) onClick(dataItem);
  };

  const ChartLegendContent = useCallback(
    (props: any) => (
      <ChartLegentContentComponent {...props} portalRef={legendPortalRef} />
    ),
    [legendPortalRef],
  );

  return (
    <StyledDonutChartContainer>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            cursor={'pointer'}
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius="40%"
            outerRadius="65%"
            onClick={handleClick}
            activeIndex={activeIndex}
            activeShape={(props: any) => renderActiveShape({ ...props, commonCount })}
            paddingAngle={1}
            startAngle={450}
            endAngle={90}
            onMouseEnter={(payload, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(undefined)}
            label={showLabels ? renderCustomizedLabel : undefined}
            isAnimationActive={isAnimationActive}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getColorByIndex(index, activeIndex === index)}
              />
            ))}
          </Pie>
          {showLegend && (
            <Legend
              align="right"
              verticalAlign="middle"
              layout="vertical"
              onClick={({ payload }) => handleClick(payload)}
              content={ChartLegendContent}
              onMouseEnter={(payload, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(undefined)}
              formatter={
                showLabels
                  ? label =>
                    renderLegendLabelFormatter(label, data, commonCount)
                  : undefined
              }
            />
          )}
        </PieChart>
      </ResponsiveContainer>
    </StyledDonutChartContainer>
  );
};

export default DonutChartComponent;
